import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import {
  Users,
  Briefcase,
  FlaskConical,
  GraduationCap,
  TrendingUp,
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
  ArrowUpRight,
  Activity,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const General = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/stats`, {
          withCredentials: true,
        });
        setStats(response.data);
      } catch (error) {
        // Silently handle expected 404 errors
        if (error.response?.status !== 404) {
          console.error("Error fetching stats:", error);
        }
        // Initialize with empty structure for production
        setStats({
          total_members: 0,
          total_deals: 0,
          total_projects: 0,
          total_courses: 0,
          total_students: 0,
          deals_by_stage: {},
          tasks_by_status: {},
          projects_by_status: {},
        });
      } finally {
        setLoading(false);
      }
    };

    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/upcoming-events`, {
          withCredentials: true,
        });
        setUpcomingEvents(response.data || []);
      } catch (error) {
        // Silently handle expected 404 errors
        if (error.response?.status !== 404) {
          console.error("Error fetching upcoming events:", error);
        }
        setUpcomingEvents([]);
      }
    };

    const fetchActivity = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/recent-activity`, {
          withCredentials: true,
        });
        setRecentActivity(response.data || []);
      } catch (error) {
        // Silently handle expected 404 errors
        if (error.response?.status !== 404) {
          console.error("Error fetching recent activity:", error);
        }
        setRecentActivity([]);
      }
    };

    fetchStats();
    fetchEvents();
    fetchActivity();
  }, []);

  const statCards = [
    {
      label: "Miembros del Equipo",
      value: stats?.total_members || 0,
      icon: Users,
      color: "text-primary",
      bgColor: "bg-primary/10",
      change: stats?.members_change || "",
    },
    {
      label: "Deals Activos",
      value: stats?.total_deals || 0,
      icon: Briefcase,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
      change: stats?.deals_change || "",
    },
    {
      label: "Proyectos",
      value: stats?.total_projects || 0,
      icon: FlaskConical,
      color: "text-violet-600",
      bgColor: "bg-violet-50",
      change: stats?.projects_change || "",
    },
    {
      label: "Estudiantes",
      value: stats?.total_students || 0,
      icon: GraduationCap,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      change: stats?.students_change || "",
    },
  ];

  const taskData = stats?.tasks_by_status
    ? [
        { name: "Backlog", value: stats.tasks_by_status.backlog || 0, color: "#94A3B8" },
        { name: "Por hacer", value: stats.tasks_by_status.todo || 0, color: "#6C8ECC" },
        { name: "En progreso", value: stats.tasks_by_status.in_progress || 0, color: "#F59E0B" },
        { name: "Revisión", value: stats.tasks_by_status.review || 0, color: "#8B5CF6" },
        { name: "Completado", value: stats.tasks_by_status.done || 0, color: "#10B981" },
      ]
    : [];

  const dealsPipelineData = stats?.deals_by_stage
    ? [
        { stage: "Diagnóstico", count: stats.deals_by_stage.diagnostico?.count || 0, value: stats.deals_by_stage.diagnostico?.value || 0 },
        { stage: "Activación", count: stats.deals_by_stage.activacion?.count || 0, value: stats.deals_by_stage.activacion?.value || 0 },
        { stage: "Ruta", count: stats.deals_by_stage.ruta?.count || 0, value: stats.deals_by_stage.ruta?.value || 0 },
        { stage: "Cierre", count: stats.deals_by_stage.cierre?.count || 0, value: stats.deals_by_stage.cierre?.value || 0 },
      ]
    : [];

  // State for dynamic data from backend
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="general">
      {/* Welcome Section */}
      <div className="glass-panel p-8 bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 mb-2">
              ¡Hola, {user?.name?.split(" ")[0] || "Usuario"}! 👋
            </h1>
            <p className="text-slate-600">
              Bienvenido a la Dirección General del Ecosistema WCA
            </p>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-slate-500">Fecha actual</p>
              <p className="font-semibold text-slate-800">
                {new Date().toLocaleDateString("es-ES", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Calendar size={24} className="text-primary" />
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className="glass-panel p-6 card-hover"
            data-testid={`stat-card-${index}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`w-12 h-12 ${stat.bgColor} rounded-xl flex items-center justify-center`}>
                <stat.icon size={24} className={stat.color} />
              </div>
              {stat.change && (
                <div className="flex items-center gap-1 text-emerald-600 text-sm font-medium">
                  <TrendingUp size={16} />
                  <span>{stat.change}</span>
                </div>
              )}
            </div>
            <p className="stat-value">{stat.value.toLocaleString()}</p>
            <p className="stat-label">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pipeline Chart */}
        <div className="lg:col-span-2 glass-panel p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-slate-800">Pipeline de Ventas</h2>
            <button className="btn-ghost text-sm flex items-center gap-1">
              Ver todo <ArrowUpRight size={16} />
            </button>
          </div>
          <div className="h-80" style={{ minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dealsPipelineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="stage" tick={{ fill: "#64748B", fontSize: 12 }} />
                <YAxis tick={{ fill: "#64748B", fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "none",
                    borderRadius: "12px",
                    boxShadow: "0 4px 24px rgba(0,0,0,0.1)",
                  }}
                  formatter={(value, name) => [
                    name === "count" ? `${value} deals` : `$${value.toLocaleString()}`,
                    name === "count" ? "Cantidad" : "Valor",
                  ]}
                />
                <Bar dataKey="count" fill="#6C8ECC" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Tasks Distribution */}
        <div className="glass-panel p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-6">Estado de Tareas</h2>
          <div className="h-64" style={{ minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={taskData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={4}
                  dataKey="value"
                >
                  {taskData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "none",
                    borderRadius: "12px",
                    boxShadow: "0 4px 24px rgba(0,0,0,0.1)",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-4">
            {taskData.map((item, index) => (
              <div key={index} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span className="text-slate-600">{item.name}</span>
                </div>
                <span className="font-medium text-slate-800">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Events */}
        <div className="glass-panel p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-slate-800">Próximos Eventos</h2>
            <button className="btn-ghost text-sm flex items-center gap-1">
              Calendario <ArrowUpRight size={16} />
            </button>
          </div>
          <div className="space-y-4">
            {upcomingEvents.length > 0 ? (
              upcomingEvents.map((event, index) => (
                <div
                  key={event.id || index}
                  className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50/50 hover:bg-slate-100/50 transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Clock size={20} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-800">{event.title}</p>
                    <p className="text-sm text-slate-500">
                      {event.date} • {event.time}
                    </p>
                  </div>
                  <span className="badge badge-primary">{event.type}</span>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Calendar size={32} className="mx-auto mb-2" />
                <p className="text-sm">No hay eventos próximos</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="glass-panel p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-slate-800">Actividad Reciente</h2>
            <button className="btn-ghost text-sm flex items-center gap-1">
              Ver todo <ArrowUpRight size={16} />
            </button>
          </div>
          <div className="space-y-4">
            {recentActivity.length > 0 ? (
              recentActivity.map((activity, index) => {
                // Handle icon - can be a component or string
                const IconComponent = typeof activity.icon === 'function' ? activity.icon :
                  activity.icon === 'Briefcase' ? Briefcase :
                  activity.icon === 'CheckCircle2' ? CheckCircle2 :
                  activity.icon === 'Users' ? Users :
                  activity.icon === 'FlaskConical' ? FlaskConical : Activity;
                
                return (
                  <div
                    key={activity.id || index}
                    className="flex items-start gap-4 p-4 rounded-2xl hover:bg-slate-50/50 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <IconComponent size={18} className="text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800">{activity.action}</p>
                      <p className="text-sm text-slate-500 truncate">{activity.detail}</p>
                    </div>
                    <span className="text-xs text-slate-400 flex-shrink-0">{activity.time}</span>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Activity size={32} className="mx-auto mb-2" />
                <p className="text-sm">No hay actividad reciente</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default General;
